import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Loader2, Users, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import Navigation from "@/components/Navigation";

interface Circle {
  id: string;
  name: string;
  description: string | null;
  creator_id: string;
  max_members: number;
}

interface Discussion {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  profile: {
    name: string;
    avatar_url: string;
  };
}

interface Member {
  id: string;
  user_id: string;
  role: string;
  profile: {
    name: string;
    avatar_url: string;
  };
}

const CircleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [circle, setCircle] = useState<Circle | null>(null);
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCircleData();
      subscribeToDiscussions();
    }
  }, [id]);

  const fetchCircleData = async () => {
    try {
      // Fetch circle details
      const { data: circleData, error: circleError } = await supabase
        .from('learning_circles')
        .select('*')
        .eq('id', id)
        .single();

      if (circleError) throw circleError;
      setCircle(circleData);

      // Fetch members
      const { data: membersData, error: membersError } = await supabase
        .from('learning_circle_members')
        .select('id, user_id, role')
        .eq('circle_id', id);

      if (membersError) throw membersError;

      // Fetch profiles for members
      const userIds = membersData?.map(m => m.user_id) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, name, avatar_url')
        .in('id', userIds);

      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
      const membersWithProfiles = membersData?.map(m => ({
        ...m,
        profile: profilesMap.get(m.user_id) || { name: 'Anonymous', avatar_url: '' }
      })) || [];

      setMembers(membersWithProfiles);

      // Fetch discussions
      await fetchDiscussions();
    } catch (error: any) {
      console.error('Error fetching circle data:', error);
      toast({
        title: "Error",
        description: "Failed to load circle details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscussions = async () => {
    try {
      const { data, error } = await supabase
        .from('circle_discussions')
        .select('id, user_id, content, created_at')
        .eq('circle_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch profiles for discussion authors
      const userIds = [...new Set(data?.map(d => d.user_id) || [])];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, name, avatar_url')
        .in('id', userIds);

      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
      const discussionsWithProfiles = data?.map(d => ({
        ...d,
        profile: profilesMap.get(d.user_id) || { name: 'Anonymous', avatar_url: '' }
      })) || [];

      setDiscussions(discussionsWithProfiles);
    } catch (error: any) {
      console.error('Error fetching discussions:', error);
    }
  };

  const subscribeToDiscussions = () => {
    const channel = supabase
      .channel(`circle_discussions:${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'circle_discussions',
          filter: `circle_id=eq.${id}`
        },
        () => {
          fetchDiscussions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('circle_discussions')
        .insert({
          circle_id: id,
          user_id: user.id,
          content: newMessage.trim()
        });

      if (error) throw error;

      setNewMessage("");
      toast({
        title: "Message sent!",
        description: "Your message has been posted to the circle"
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  if (!circle) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="container max-w-md mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">Circle not found</p>
          <Button onClick={() => navigate('/circles')} className="mt-4">
            Back to Circles
          </Button>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="gradient-primary text-white p-4 sticky top-0 z-10 shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/circles')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">{circle.name}</h1>
              {circle.description && (
                <p className="text-sm text-white/80">{circle.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4" />
            <span>{members.length}/{circle.max_members} members</span>
          </div>
        </div>
      </header>

      {/* Members Section */}
      <div className="container max-w-md mx-auto px-4 py-4">
        <Card className="p-4 mb-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Members
          </h3>
          <div className="flex flex-wrap gap-2">
            {members.map((member) => (
              <div key={member.id} className="flex items-center gap-2 bg-secondary/50 rounded-full px-3 py-1">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={member.profile.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.user_id}`} />
                  <AvatarFallback><User className="h-3 w-3" /></AvatarFallback>
                </Avatar>
                <span className="text-sm">{member.profile.name}</span>
                {member.role === 'creator' && (
                  <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Creator</span>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Discussions */}
        <Card className="p-4 flex flex-col h-[calc(100vh-350px)]">
          <h3 className="font-semibold mb-3">Discussions</h3>
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4">
              {discussions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No messages yet. Start the conversation!
                </p>
              ) : (
                discussions.map((discussion) => (
                  <div key={discussion.id} className="flex gap-3">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarImage src={discussion.profile.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${discussion.user_id}`} />
                      <AvatarFallback><User className="h-4 w-4" /></AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold">{discussion.profile.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(discussion.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm bg-secondary/50 rounded-lg p-2">
                        {discussion.content}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          
          {/* Message Input */}
          {user ? (
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <Textarea
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                className="min-h-[60px] max-h-[120px]"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                size="icon"
                className="flex-shrink-0"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          ) : (
            <div className="mt-4 pt-4 border-t text-center">
              <p className="text-sm text-muted-foreground mb-2">Login to participate in discussions</p>
              <Button size="sm" onClick={() => navigate('/auth')}>
                Login or Signup
              </Button>
            </div>
          )}
        </Card>
      </div>

      <Navigation />
    </div>
  );
};

export default CircleDetail;